import React, { useEffect, useState } from 'react';
import ResourceService, {
  MobileUserResource,
  ResourceProps
} from 'services/hooks/resource-service';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import useUpdateManageResources from './ResourceForms/crud/useUpdateManageResources';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import Modal from 'components/elements/Modal';
import MessageOccupant from '../Notification/MessageOccupant';
import BackIcon from 'assets/Icons/modals/BackIcon';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import ManageIcon from 'assets/Icons/modals/ManageIcon';
import variables from 'variables';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import CancelIcon from 'assets/Icons/modals/CancelIcon';
import MessageIcon from '@mui/icons-material/Message';
import { colours } from 'utils/constants';
import { Grid, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage, generateUpdateSuccessMessage } from 'utils/helpers';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';

const ManageResourcesMode: React.FC = () => {
  /* Below code is using the `useParams` hook from the `react-router-dom` library to extract the value of the `resourceUUID` parameter from the current URL. 
  It allows the component to access the value of the `resourceUUID` parameter that is passed in the URL. */
  const { resourceUUID } = useParams();
  /* This function can be used to programmatically navigate to different routes in the application. */
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [openMessageModal, setopenMessageModal] = useState(false);
  const [mobileUsers, setMobileUsers] = useState<MobileUserResource[] | undefined>([]);
  const [resourceInfo, setResourceInfo] = useState<ResourceProps | undefined>();
  const [getBuildingsResult, setGetBuildingsResult] = useState<BuildingProps[] | null>([]);

  const {
    refetch,
    data: resourceData,
    isLoading
  } = useQuery<ResourceProps, Error>(
    'resource',
    () => ResourceService.findResourceById(resourceUUID),
    {
      enabled: !!resourceUUID,
      onSuccess: (data) => {
        setMobileUsers(data.mobileUsers);
        setResourceInfo(data);
      },
      onError: () => {
        toast.error(generateApiFailErrorMessage('Amenities'));
      }
    }
  );

  const { refetch: buildingRefetch, data: buildingData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onSuccess: (data) => {
        setGetBuildingsResult(data);
      },
      onError: () => {
        toast.error(generateApiFailErrorMessage('Buildings'));
      }
    }
  );

  useEffect(() => {
    if (resourceData) {
      setMobileUsers(resourceData?.mobileUsers);
      setResourceInfo(resourceData);
    }
    if (buildingData && Array.isArray(buildingData)) {
      setGetBuildingsResult(buildingData);
    }
  }, [resourceData, buildingData]);

  // Helper function to check if the parent checkbox should be pre-selected
  const shouldParentCheckboxBeSelected = (OrganizationUuid: string) => {
    const usersForOrganization = mobileUsers?.filter(
      (user) => user.organizationUuid === OrganizationUuid
    );
    return usersForOrganization?.some((user) => user.permission) || false;
  };

  // Handler for parent checkbox change
  const handleParentCheckboxChange =
    (OrganizationUuid: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const updatedMobileUsers = mobileUsers?.map((user) => ({
        ...user,
        permission: user.organizationUuid === OrganizationUuid ? checked : user.permission
      }));
      setMobileUsers(updatedMobileUsers);
    };

  // Handler for nested checkbox change
  const handleNestedCheckboxChange =
    (mobileUserUuid: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const updatedMobileUsers = mobileUsers?.map((user) =>
        user.mobileUserUuid === mobileUserUuid ? { ...user, permission: checked } : user
      );
      setMobileUsers(updatedMobileUsers);
    };

  // Handler for 'Select All' checkbox change
  const handleSelectAllCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const updatedMobileUsers = mobileUsers?.map((user) => ({
      ...user,
      permission: checked
    }));
    setMobileUsers(updatedMobileUsers);
  };

  /* The code is creating a mutation function called `updateManageResourceMutation` using the `useUpdateManageResources` hook. 
  This hook takes an object with three properties: */
  const updateManageResourceMutation = useUpdateManageResources({
    updateQuery: ResourceService.updateManageResource,
    queryName: 'updateResourcePermissions',
    onSuccess: () => {
      refetch();
      buildingRefetch();
      toast.success(generateUpdateSuccessMessage('Resource Permissions'));
    },
    onError: async () => {
      await refetch();
      toast.error('Failed to update Resource Permissions. Please try again');
    }
  });

  /* The handleSubmit function is used to save the updated manage resource information using `updateManageResourceMutation` 
  mutation with the given resourceUUID and mobileUsers. */
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    updateManageResourceMutation.mutate({ resourceUUID, mobileUsers: mobileUsers });
  };

  // Group mobileUsers by OrganizationUuid to get the list of unique OrganizationNames
  const uniqueOrganizationNames = Array.from(
    new Set(mobileUsers?.map((user) => user?.organizationUuid))
  );

  return !isLoading ? (
    <>
      <div className="flex my-10 items-center justify-between">
        <SecondaryButton
          buttonLabel="Back"
          iconLeft={<BackIcon />}
          onClick={() => navigate('/resources')}
          variant="default"
          size="px-4 w-auto"
          type="button"
        />
        <div className="flex items-center space-x-2 md:space-x-4 ml-2 md:ml-4">
          <Grid item>
            {isSmallScreen ? (
              <Tooltip title="Message Occupant">
                <IconButton
                  onClick={() => setopenMessageModal(true)}
                  size="large"
                  sx={{
                    backgroundColor: '#FFFFFF',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}
                >
                  <MessageIcon
                    sx={{
                      color: colours.seafoam
                    }}
                  />
                </IconButton>
              </Tooltip>
            ) : (
              <SecondaryButton
                buttonLabel="Message Occupant"
                iconLeft={<MessageIcon sx={{ color: colours.seafoam }} />}
                onClick={() => setopenMessageModal(true)}
                variant="default"
                size="px-4 w-auto !bg-primary2"
              />
            )}
          </Grid>
        </div>
      </div>
      <Modal
        isOpen={openMessageModal}
        onRequestClose={() => setopenMessageModal(false)}
        title="Send Message to Occupants"
      >
        <MessageOccupant setShowModal={setopenMessageModal} />
      </Modal>

      <h1>Manage Amenity Permissions</h1>

      <form onSubmit={handleSubmit}>
        <div className="flex gap-32 bg-primary2 px-6 py-6">
          <div className="w-2/5">
            {resourceInfo?.image ? (
              <img
                src={variables.RESOURCE_IMAGE_URL + resourceInfo?.image}
                className="m-0 p-0 h-72 max-w-full rounded-lg"
              ></img>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="m-0 p-0 h-72 max-w-full rounded-lg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                />
              </svg>
            )}

            <div className="mt-3 text-xl">
              <b>{resourceInfo?.name}</b>
            </div>
            <div className="mt-2 text-base">Type: {resourceInfo?.type}</div>
            <div className="mt-2 text-base">
              Building:{' '}
              {
                getBuildingsResult?.find(
                  (f: { uuid?: string }) => f?.uuid === resourceInfo?.buildingUuid
                )?.name
              }
            </div>
            <br />
            <div className="flex gap-2.5 ml-2">
              <PrimaryButton
                buttonLabel="Save Permissions"
                iconLeft={<ManageIcon />}
                variant="default"
                size={'mt-4 px-6 w-auto gap-2'}
              />
              <SecondaryButton
                buttonLabel="Discard"
                iconLeft={<CancelIcon />}
                onClick={() => navigate('/resources')}
                variant="default"
                size={
                  'mt-4 px-6 w-auto gap-2 transition-colors duration-300 transform sm:inline hover:bg-secondary2'
                }
                type="button"
              />
            </div>
          </div>
          <div>
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={mobileUsers?.every((user) => user.permission)}
                onChange={handleSelectAllCheckboxChange}
              />
              Select All
            </label>
            <br />
            {uniqueOrganizationNames.map((organizationUuid) => (
              <div key={organizationUuid}>
                <label className="flex gap-2 ml-8">
                  <input
                    type="checkbox"
                    checked={shouldParentCheckboxBeSelected(organizationUuid)}
                    onChange={handleParentCheckboxChange(organizationUuid)}
                  />
                  {
                    mobileUsers?.find(
                      (f: { organizationUuid: string }) => f.organizationUuid === organizationUuid
                    )?.organizationName
                  }
                </label>
                {mobileUsers
                  ?.filter((user) => user?.organizationUuid === organizationUuid)
                  .map((user) => (
                    <label className="flex mt-4 gap-2 ml-16" key={user.mobileUserUuid}>
                      <input
                        type="checkbox"
                        checked={user.permission}
                        onChange={handleNestedCheckboxChange(user.mobileUserUuid)}
                      />
                      {user?.occupantName}
                    </label>
                  ))}
                <br />
              </div>
            ))}
          </div>
        </div>
      </form>
    </>
  ) : (
    <div>
      <LoadingSpinner />
    </div>
  );
};

export default ManageResourcesMode;
